'use client';

import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useRouter } from 'next/navigation';
import Input from '@/ui/input/input';
import Button from '@/ui/button/button';
import { FORGOTTEN_PASSWORD_ROUTE } from '@/route';

export interface LoginCardProps {
  className?: string;
  title?: string;
  showForgotPassword?: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  emailValue: string;
  emailError?: string;
  onEmailChange: (value: string) => void;
  onEmailBlur?: () => void;
  emailPlaceholder?: string;
  passwordValue: string;
  passwordError?: string;
  onPasswordChange: (value: string) => void;
  onPasswordBlur?: () => void;
  passwordPlaceholder?: string;
  showPassword: boolean;
  onTogglePassword: () => void;
  isSubmitting: boolean;
  error?: string;
  forgotPasswordRoute?: string;
  buttonText?: string;
}

export const LoginCard: React.FC<LoginCardProps> = ({
  className = '',
  title = 'Connexion à votre espace',
  showForgotPassword = true,
  onSubmit,
  emailValue,
  emailError,
  onEmailChange,
  onEmailBlur,
  emailPlaceholder = 'Votre adresse mail',
  passwordValue,
  passwordError,
  onPasswordChange,
  onPasswordBlur,
  passwordPlaceholder = 'Votre mot de passe',
  showPassword,
  onTogglePassword,
  isSubmitting,
  error,
  forgotPasswordRoute = FORGOTTEN_PASSWORD_ROUTE,
  buttonText = 'Se connecter',
}) => {
  const router = useRouter();

  const handleForgotPasswordClick = () => {
    router.push(forgotPasswordRoute);
  };

  return (
    <div className={`w-full max-w-[400px] ${className}`}>
      {title && (
        <h1 className="text-2xl font-bold text-gray-900 mb-8 text-center w-full">
          {title}
        </h1>
      )}

      <form onSubmit={onSubmit} method="post" className="w-full space-y-5">
        <Input
          label="Adresse mail"
          placeholder={emailPlaceholder}
          type="email"
          size="medium"
          state={emailError ? 'error' : 'default'}
          helperText={emailError}
          showLeftIcon={false}
          showRightIcon={false}
          value={emailValue}
          onChange={(e) => onEmailChange(e.target.value)}
          onBlur={onEmailBlur}
        />

        <div className="space-y-1">
          <Input
            label="Mot de passe"
            placeholder={passwordPlaceholder}
            type={showPassword ? 'text' : 'password'}
            size="medium"
            state={passwordError ? 'error' : 'default'}
            helperText={passwordError}
            showLeftIcon={false}
            showRightIcon={false}
            value={passwordValue}
            onChange={(e) => onPasswordChange(e.target.value)}
            onBlur={onPasswordBlur}
            rightElement={
              <button
                type="button"
                onClick={onTogglePassword}
                className="text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            }
          />

          {showForgotPassword && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleForgotPasswordClick}
                className="text-sm text-neutral-800 hover:text-orange-500 transition-colors mt-2"
              >
                Mot de passe oublié ?
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="text-sm text-red-600 text-center mt-2">
            {error}
          </div>
        )}

        <Button 
          type="submit" 
          variant="primary" 
          size="medium" 
          className="w-full mt-2"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Connexion...' : buttonText}
        </Button>
      </form>
    </div>
  );
};

LoginCard.displayName = 'LoginCard';

export default LoginCard;

